import React, { useEffect, useState } from 'react';

import BoxConteudo from '../../../../../../../components/BoxConteudo';
import CustomButton from '../../../../../../../components/CustomButton';
import CustomCol from '../../../../../../../components/CustomCol';
import CustomInputText from '../../../../../../../components/CustomInputs/CustomInputText';
import CustomModal from '../../../../../../../components/CustomModal';
import CustomOffcanvas from '../../../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../../../components/CustomRow';
import CustomSelect from '../../../../../../../components/CustomSelects/CustomSelect';
import Log from '../../../../../../../components/Log/Log';
import {
  IModuloItemForm,
  IModuloItemRegistro,
} from '../../../../../../../modules/types/ModuloSisaf';
import theme from '../../../../../../../theme';
import { formataNomeMeioSiglas } from '../../../../../../../utils/functions';
import { handleSave } from '../../../../../../../utils/handleSave';
import { optionStatusBin } from '../../../../Dominios/utils/optionStatusBin';

interface ModuloItemFormProps {
  submoduloId: number;
  registro: IModuloItemRegistro | null;
  onClose: () => void;
  onSaved: (savedForm: IModuloItemForm) => void;
  onDeleted: (id: number) => void;
  asModal?: boolean;
}

const ModuloItemForm: React.FC<ModuloItemFormProps> = ({
  submoduloId,
  registro,
  onClose,
  onSaved,
  onDeleted,
  asModal = false,
}) => {
  const [logRefresh, setLogRefresh] = useState(0);
  const camposObrigatorios = {
    status: 'Status',
    item: 'Nome do Item',
  };

  const camposAjustar = {
    number: ['id', 'submoduloId', 'status'],
    string: ['item', 'descricao'],
  };

  const [form, setForm] = useState<IModuloItemForm>({
    id: 0,
    submoduloId,
    status: 0,
    item: '',
    descricao: '',
  });

  useEffect(() => {
    if (registro) {
      setForm({
        id: registro.id,
        submoduloId: registro.submodulo?.id || submoduloId,
        status: registro.status,
        item: registro.item,
        descricao: registro.descricao || '',
      });
      return;
    }

    setForm({
      id: 0,
      submoduloId,
      status: 0,
      item: '',
      descricao: '',
    });
  }, [registro, submoduloId]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;
    const parsedValue = name === 'status' ? Number(value) : value;
    setForm((prev) => ({ ...prev, [name]: parsedValue }));
  };

  const handleSubmit = async () => {
    const saved = await handleSave(
      { ...form, submoduloId },
      camposObrigatorios,
      camposAjustar,
      'moduloitens',
      'daf/adm/modulossiaf/modulo',
      undefined,
      false,
    );

    if (saved) {
      setLogRefresh((prev) => prev + 1);
      onSaved({ ...form, submoduloId });
    }
  };

  const FormContent = (
    <BoxConteudo boxTitulo="Identificação do Item">
      <CustomRow>
        <CustomCol width="200px">
          <CustomSelect
            name="status"
            options={optionStatusBin}
            title="Status"
            value={form.status}
            onChange={handleChange}
            style={{
              backgroundColor:
                Number(form.status) === 2
                  ? theme.colors.vermelho
                  : Number(form.status) === 1
                    ? theme.colors.verde
                    : 'white',
              color: Number(form.status) === 0 ? '#000000' : '#ffffff',
            }}
            required
          />
        </CustomCol>
        <CustomCol>
          <CustomInputText
            id="item"
            name="item"
            value={form.item}
            title="Nome do Item"
            onChange={handleChange}
            required
          />
        </CustomCol>
      </CustomRow>
      <CustomRow>
        <CustomCol>
          <CustomInputText
            id="descricaoItem"
            name="descricao"
            value={form.descricao || ''}
            title="Descrição"
            type="textArea"
            rows={3}
            onChange={handleChange}
          />
        </CustomCol>
      </CustomRow>

      <CustomRow
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '16px',
          flexWrap: 'nowrap',
          alignItems: 'center',
        }}
      >
        {form.id !== 0 && (
          <CustomButton
            label="Excluir"
            bgColor={theme.colors.vermelho}
            textColor={theme.colors.white}
            style={{ minWidth: '110px', width: '120px' }}
            onClick={() => onDeleted(form.id)}
          />
        )}
        <CustomButton
          label="Salvar"
          bgColor={theme.colors.botaoAcao.azul}
          textColor={theme.colors.white}
          style={{ minWidth: '110px', width: '120px' }}
          onClick={handleSubmit}
        />
      </CustomRow>
    </BoxConteudo>
  );

  const logContent =
    registro && registro.id !== 0 ? (
      <div style={{ marginBottom: '12px' }}>
        <Log
          id={registro.id}
          dataRegistro={registro.dataRegistro}
          usuarioRegistro={formataNomeMeioSiglas(
            registro.usuarioRegistro?.nomeCompleto,
          )}
          dataAtualizacao={registro.dataUltimaAtualizacao}
          usuarioAtualizacao={formataNomeMeioSiglas(
            registro.usuarioAtualizacao?.nomeCompleto,
          )}
          nEdicoes={registro.nEdicoes}
          entity="TB_MODULO_ITEM"
          refreshTriggerLog={logRefresh}
        />
      </div>
    ) : null;

  if (asModal) {
    return (
      <CustomModal
        open
        onClose={onClose}
        title={form.id ? 'Editar Item' : 'Novo Item'}
        size="large"
        data-testid="modal-item-submodulo"
      >
        {logContent}
        {FormContent}
      </CustomModal>
    );
  }

  return (
    <CustomOffcanvas
      show
      onClose={onClose}
      title={form.id ? 'Editar Item' : 'Novo Item'}
      width="40%"
      showOpenButton={false}
    >
      {logContent}
      {FormContent}
    </CustomOffcanvas>
  );
};

export default ModuloItemForm;
